import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiCheck, mdiPaperclip } from "@mdi/js";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InvoiceAttachments } from "../../../../components/molecules/InvoiceAttachments";
import { VendorProfileBanner } from "../../../../components/molecules/VendorProfileBanner";
import {
  fetchInvoiceApproval,
  updateInvoiceApproval,
} from "../../../../services/invoiceApprovals";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

const SuccessMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        paddingTop: 3,
      }}
    >
      <Card
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 475,
        }}
      >
        <CardContent
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Icon color="success" icon={mdiCheck} sx={{ fontSize: "4.5rem" }} />

          <Typography variant="h6">
            {t("invoiceApproval.successTitle")}
          </Typography>

          <Typography variant="body1">
            {t("invoiceApproval.successMessage")}
          </Typography>
        </CardContent>
        <CardActions>
          <Button onClick={() => window.close()} size="small">
            {t("common.close")}
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};

type FormData = {
  comment: string;
};
interface DecisionModalProps {
  invoiceApprovalId: string;
  isApproving: boolean;
  onClose: () => void;
  open: boolean;
  setShowSuccessMessage: (show: boolean) => void;
}
const DecisionModal: React.FC<DecisionModalProps> = ({
  invoiceApprovalId,
  isApproving,
  onClose,
  open,
  setShowSuccessMessage,
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [loading, setLoading] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    defaultValues: {
      comment: "",
    },
  });

  const onSubmit = useCallback(
    (data: FormData): void => {
      setLoading(() => true);
      updateInvoiceApproval(invoiceApprovalId, {
        comment: data.comment,
        status: isApproving ? "approved" : "rejected",
      })
        .then(() => {
          router.invalidate();
          setShowSuccessMessage(true);
          onClose();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [invoiceApprovalId, isApproving, onClose, router, setShowSuccessMessage],
  );

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>
        {isApproving
          ? t("invoiceApproval.approveTitle")
          : t("invoiceApproval.rejectTitle")}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                error={!!errors.comment}
                fullWidth
                helperText={
                  errors.comment && t("invoiceApproval.commentRequired")
                }
                label={t("entities.invoiceApproval.fields.comment")}
                margin="dense"
                multiline
                rows={4}
                type="text"
              />
            )}
            rules={{ required: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            color={isApproving ? "success" : "error"}
            loading={loading}
            type="submit"
            variant="contained"
          >
            {isApproving
              ? t("invoiceApproval.confirmApprove")
              : t("invoiceApproval.confirmReject")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const InvoiceValue: React.FC<{ currency?: string; value?: number }> = ({
  currency,
  value,
}) => {
  const { i18n, t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.value")}
      </Typography>
      <Typography>
        {value === undefined || currency === undefined ? (
          <Skeleton />
        ) : (
          `${value.toLocaleString(i18n.language, {
            currency,
            style: "currency",
          })} (${currency})`
        )}
      </Typography>
    </Box>
  );
};

const InvoiceCreatedAt: React.FC<{ createdAt?: string }> = ({ createdAt }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.createdAt")}
      </Typography>
      <Typography>
        {createdAt === undefined ? (
          <Skeleton />
        ) : (
          t("intlDateTime", i18nextParseToDate(createdAt))
        )}
      </Typography>
    </Box>
  );
};

const InvoicePurchaseOrderNumber: React.FC<{
  purchaseOrderNumber?: string;
}> = ({ purchaseOrderNumber }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.purchaseOrderNumber")}
      </Typography>
      <Typography variant="body1">
        {purchaseOrderNumber ?? <Skeleton />}
      </Typography>
    </Box>
  );
};

const InvoiceDiNumber: React.FC<{ diNumber?: string }> = ({ diNumber }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.diNumber")}
      </Typography>
      <Typography variant="body1">{diNumber ?? <Skeleton />}</Typography>
    </Box>
  );
};

const InvoicePurchaseGroup: React.FC<{ purchaseGroup?: string }> = ({
  purchaseGroup,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.purchaseGroup")}
      </Typography>
      <Typography variant="body1">{purchaseGroup ?? <Skeleton />}</Typography>
    </Box>
  );
};

const InvoiceUrgency: React.FC<{ urgency?: string }> = ({ urgency }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.urgency")}
      </Typography>
      <Typography variant="body1">{urgency ?? <Skeleton />}</Typography>
    </Box>
  );
};

const InvoiceInvoiceType: React.FC<{ invoiceType?: string }> = ({
  invoiceType,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.invoiceType")}
      </Typography>
      <Typography variant="body1">{invoiceType ?? <Skeleton />}</Typography>
    </Box>
  );
};

const InvoiceShore: React.FC<{ shore?: string }> = ({ shore }) => {
  const { t } = useTranslation();

  const label = useMemo(() => {
    if (shore === undefined) {
      return <Skeleton />;
    }

    if (shore === "") {
      return "";
    }

    return t(`entities.invoice.options.shore.${shore}`);
  }, [shore, t]);

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.shore")}
      </Typography>
      <Typography variant="body1">{label}</Typography>
    </Box>
  );
};

const InvoiceBaseArea: React.FC<{ baseArea?: string }> = ({ baseArea }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.baseArea")}
      </Typography>
      <Typography variant="body1">
        {baseArea === undefined ? (
          <Skeleton />
        ) : (
          t(`entities.invoice.options.baseArea.${baseArea}`)
        )}
      </Typography>
    </Box>
  );
};

const InvoiceReceiveBase: React.FC<{ receiveBase?: number }> = ({
  receiveBase,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.receivingBase")}
      </Typography>
      <Typography variant="body1">
        {receiveBase === undefined ? (
          <Skeleton />
        ) : (
          t(`entities.invoice.options.receivingBase.${receiveBase}`)
        )}
      </Typography>
    </Box>
  );
};

const InvoiceContainers: React.FC<{ containers?: string[] }> = ({
  containers,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.containers")}
      </Typography>
      <Typography variant="body1">
        {containers === undefined ? <Skeleton /> : containers?.join(", ")}
      </Typography>
    </Box>
  );
};

const InvoiceVolume: React.FC<{ volume?: number }> = ({ volume }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        sx={{
          color: "text.secondary",
        }}
        variant="subtitle2"
      >
        {t("entities.invoice.fields.volume")}
      </Typography>
      <Typography variant="body1">{volume ?? <Skeleton />}</Typography>
    </Box>
  );
};

const ShowInvoiceApproval: React.FC = () => {
  const { t } = useTranslation();

  const invoiceApproval = Route.useLoaderData();

  const [currentTab, setCurrentTab] = useState("invoiceAttachments");
  const [isApproving, setIsApproving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleOpenModal = (approving: boolean): void => {
    setIsApproving(approving);
    setModalOpen(true);
  };

  // Reset success message when navigating to different invoice approval
  useEffect(() => {
    setShowSuccessMessage(false);
  }, []);

  if (showSuccessMessage) {
    return <SuccessMessage />;
  }

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        justifyContent: "center",
        margin: "0 auto",
        maxWidth: "1200px",
        padding: "16px",
        width: "100%",
      }}
    >
      <Card
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          padding: "12px",
          width: "100%",
        }}
      >
        <Typography component="div" variant="h6">
          {invoiceApproval ? (
            `${t("invoiceApproval.title")} - ${invoiceApproval.invoice.remote ? t("invoiceApproval.remoteInvoice") : ""} #${invoiceApproval.invoice.invoice_number}${invoiceApproval.invoice.invoice_series ? `-${invoiceApproval.invoice.invoice_series}` : ""}`
          ) : (
            <Skeleton width={600} />
          )}
        </Typography>
      </Card>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: "16px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          <InvoiceValue
            currency={invoiceApproval?.invoice.currency.name}
            value={invoiceApproval?.invoice.value}
          />

          <InvoiceCreatedAt createdAt={invoiceApproval?.invoice.created_at} />

          <InvoicePurchaseOrderNumber
            purchaseOrderNumber={
              invoiceApproval?.invoice.purchase_order.po_number
            }
          />

          <InvoiceDiNumber diNumber={invoiceApproval?.invoice.di_number} />

          <InvoicePurchaseGroup
            purchaseGroup={invoiceApproval?.invoice.purchase_group.name}
          />

          <InvoiceUrgency
            urgency={
              invoiceApproval
                ? invoiceApproval.invoice.urgency?.label || ""
                : undefined
            }
          />

          <InvoiceInvoiceType
            invoiceType={invoiceApproval?.invoice.invoice_type.label}
          />

          <InvoiceShore shore={invoiceApproval?.invoice.shore} />

          <InvoiceBaseArea baseArea={invoiceApproval?.invoice.base_area} />

          <InvoiceReceiveBase
            receiveBase={invoiceApproval?.invoice.receiving_base}
          />

          <InvoiceContainers containers={invoiceApproval?.invoice.containers} />

          <InvoiceVolume volume={invoiceApproval?.invoice.volume} />
        </Box>

        <Divider />

        {/* Vendor Information and Approval Form Section */}
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: "24px",
          }}
        >
          {!!invoiceApproval?.invoice?.vendor?.id && (
            <VendorProfileBanner
              vendorId={invoiceApproval?.invoice?.vendor?.id}
            />
          )}

          <Divider flexItem orientation="vertical" />

          {/* Approval Form */}
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 2,
                justifyContent: "space-around",
              }}
            >
              {invoiceApproval?.status === "pending" ? (
                <>
                  <Button
                    color="success"
                    onClick={() => handleOpenModal(true)}
                    size="large"
                    sx={{ flex: 1 }}
                    variant="contained"
                  >
                    {t("invoiceApproval.approve")}
                  </Button>
                  <Button
                    color="error"
                    onClick={() => handleOpenModal(false)}
                    size="large"
                    sx={{ flex: 1 }}
                    variant="contained"
                  >
                    {t("invoiceApproval.reject")}
                  </Button>
                </>
              ) : (
                <Typography variant="h4">
                  {invoiceApproval &&
                    t(
                      `entities.invoiceApproval.options.status.${invoiceApproval.status}`,
                    )}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Card>

      {invoiceApproval?.status === "pending" && (
        <DecisionModal
          invoiceApprovalId={invoiceApproval.id}
          isApproving={isApproving}
          onClose={() => setModalOpen(false)}
          open={modalOpen}
          setShowSuccessMessage={setShowSuccessMessage}
        />
      )}

      <Card
        sx={{
          width: "100%",
        }}
      >
        <TabContext value={currentTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={(_, value: string) => setCurrentTab(value)}
              sx={{ alignSelf: "flex-start" }}
            >
              <Tab
                icon={<Icon icon={mdiPaperclip} />}
                iconPosition="start"
                label={t("common.attachments")}
                value="invoiceAttachments"
              />
            </TabList>
          </Box>
          <TabPanel value="invoiceAttachments">
            {invoiceApproval && (
              <InvoiceAttachments invoiceId={invoiceApproval.invoice.id} />
            )}
          </TabPanel>
        </TabContext>
      </Card>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/invoice_approvals/$invoiceApprovalId/",
)({
  component: ShowInvoiceApproval,
  loader: ({ params: { invoiceApprovalId } }) =>
    fetchInvoiceApproval(invoiceApprovalId),
});
